import { absoluteOrientation, resolveHeading } from './heading-state.js';

/** One shared sensor owner. Subscribers never open their own sensor streams. */
export function createHeadingService(context, host = window) {
  const listeners = new Set(), consumers = new Set();
  let enabled = false, active = false, sample = null, availability, cleanup, staleTimer, starting = false, generation = 0, nativePaused = false;
  let state = resolveHeading({ origin: context.origin() });
  try { enabled = host.localStorage.getItem('siddur-live-direction') === 'enabled'; } catch { /* Session-only preference. */ }
  const emit = () => {
    state = resolveHeading({ sample, origin: context.origin(), enabled, active, availability, instant: context.instant(), wasAligned: state.aligned });
    listeners.forEach(listener => listener(state));
    return state;
  };
  const receive = next => { sample = next; availability = next.availability || 'available'; emit(); };
  const stop = () => {
    generation++; active = false; starting = false; cleanup?.(); cleanup = null;
    clearInterval(staleTimer); staleTimer = null; sample = null; emit();
  };
  async function reconcile(userGesture = false) {
    const wanted = enabled && consumers.size > 0 && !nativePaused && host.document.visibilityState === 'visible';
    if (!wanted) { if (active || starting) stop(); else emit(); return; }
    if (active || starting) { emit(); return; }
    const attempt = ++generation; starting = true; availability = undefined;
    try {
      const native = host.SiddurNative?.postMessage;
      const orientation = host.DeviceOrientationEvent;
      if (!native && !orientation) { availability = 'unavailable'; starting = false; emit(); return; }
      if (!native && typeof orientation.requestPermission === 'function') {
        // A reload may require another gesture even if our preference is saved.
        try {
          if (await orientation.requestPermission(true) !== 'granted') throw new Error('denied');
        } catch(error) {
          if (!userGesture) { availability = 'permission-needed'; starting = false; emit(); return; }
          throw error;
        }
      }
      if (attempt !== generation) return;
      active = true; starting = false;
      if (native) {
        const handler = event => receive(event.detail);
        host.addEventListener('siddur-native-heading', handler);
        host.SiddurNative.postMessage(JSON.stringify({ type: 'heading.start' }));
        cleanup = () => { host.removeEventListener('siddur-native-heading', handler); host.SiddurNative.postMessage(JSON.stringify({ type: 'heading.stop' })); };
      } else {
        // Safari supplies webkitCompassHeading on deviceorientation; Chromium supplies the absolute event.
        const eventName = 'ondeviceorientationabsolute' in host ? 'deviceorientationabsolute' : 'deviceorientation';
        const handler = event => {
          const next = absoluteOrientation(event, host.screen.orientation?.angle ?? host.orientation ?? 0);
          if (next) receive(next);
        };
        host.addEventListener(eventName, handler, true);
        cleanup = () => host.removeEventListener(eventName, handler, true);
      }
      staleTimer = setInterval(emit, 1000); emit();
    } catch { if (attempt === generation) { availability = 'denied'; active = false; starting = false; emit(); } }
  }
  host.document.addEventListener('visibilitychange', () => reconcile());
  host.addEventListener('pagehide', stop);
  host.addEventListener('pageshow', () => reconcile());
  host.addEventListener('siddur-native-resume', () => { nativePaused=false;reconcile(); });
  host.addEventListener('siddur-native-pause', () => { nativePaused=true;stop(); });
  return {
    subscribe(listener) { listeners.add(listener); listener(state); return () => listeners.delete(listener); },
    consume(name, visible) { visible ? consumers.add(name) : consumers.delete(name); return reconcile(); },
    enable() { enabled = true; try { host.localStorage.setItem('siddur-live-direction', 'enabled'); } catch {} return reconcile(true); },
    disable() { enabled = false; try { host.localStorage.removeItem('siddur-live-direction'); } catch {} stop(); },
    refresh: emit,
    state: () => state,
    inject: next => { if (context.qa?.()) { enabled = true; active = true; receive(next); } },
  };
}
